/**
 * Shows how far a shopper is from the free-delivery threshold, and keeps the
 * figure current as the cart changes.
 *
 * The initial value is rendered by Liquid so the component is correct before
 * JavaScript runs; this script only refreshes it after cart activity.
 */

class HohShippingProgress extends HTMLElement {
  connectedCallback() {
    this.threshold = Number(this.dataset.threshold || 0);
    this.currency = this.dataset.currency || 'GBP';
    this.locale = this.dataset.locale || 'en-GB';

    this.onCartUpdate = this.onCartUpdate.bind(this);
    document.addEventListener('cart:update', this.onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.onCartUpdate);
  }

  async onCartUpdate() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`);
      if (!response.ok) return;
      const cart = await response.json();
      this.update(cart.total_price);
    } catch {
      // Leave the last known good value in place rather than showing nothing.
    }
  }

  /**
   * @param {number} totalCents
   */
  update(totalCents) {
    if (!this.threshold) return;

    const remaining = Math.max(this.threshold - totalCents, 0);
    const pct = Math.min((totalCents / this.threshold) * 100, 100);

    const bar = this.querySelector('[data-bar]');
    const text = this.querySelector('[data-text]');

    if (bar) bar.style.setProperty('--progress', `${pct}%`);

    if (text) {
      if (remaining === 0) {
        text.textContent = this.dataset.qualifiedText || 'Your order qualifies for free UK delivery';
      } else {
        const amount = new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: remaining % 100 === 0 ? 0 : 2,
        }).format(remaining / 100);

        const template = this.dataset.remainingText || 'You are [amount] away from free UK delivery';
        text.textContent = template.replace('[amount]', amount);
      }
    }

    this.dataset.qualified = remaining === 0 ? 'true' : 'false';
  }
}

if (!customElements.get('hoh-shipping-progress')) {
  customElements.define('hoh-shipping-progress', HohShippingProgress);
}
